import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowLeft, Target, RefreshCw, Copy, Check } from 'lucide-react';
import { Header } from '../components/Header';
import { API_URL, cn } from '../lib/utils';

function QuizView() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => { fetchQuiz(); }, [quizId]);

  const fetchQuiz = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quizzes/${quizId}`, { credentials: 'include' });
      if (response.ok) setQuiz(await response.json());
      else navigate('/dashboard');
    } catch { navigate('/dashboard'); }
    finally { setLoading(false); }
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/quizzes/${quizId}/regenerate`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        // Poll for completion
        const pollQuiz = async () => {
          const r = await fetch(`${API_URL}/api/quizzes/${quizId}`, { credentials: 'include' });
          if (r.ok) {
            const q = await r.json();
            if (q.status === 'completed') {
              setQuiz(q);
              setRegenerating(false);
            } else if (q.status === 'generating') {
              setTimeout(pollQuiz, 2000);
            } else {
              setRegenerating(false);
            }
          }
        };
        setTimeout(pollQuiz, 2000);
      }
    } catch {
      setRegenerating(false);
      alert('Failed to regenerate');
    }
  };

  const calculateResults = () => {
    let totalScore = 0;
    const maxScore = quiz.questions.length * 10;
    
    for (const [qId, answer] of Object.entries(answers)) {
      const question = quiz.questions.find(q => String(q.id) === String(qId));
      if (question?.points) {
        totalScore += question.points[answer] || 0;
      }
    }
    
    const percentage = Math.round((totalScore / maxScore) * 100);
    
    let resultTier = quiz.result_tiers?.[0];
    for (const tier of quiz.result_tiers || []) {
      const [low, high] = tier.range.split('-').map(Number);
      if (percentage >= low && percentage <= high) {
        resultTier = tier;
        break;
      }
    }
    
    setResults({ score: percentage, tier: resultTier });
    setShowResults(true);
  };

  const shareLink = `${window.location.origin}/take-quiz/${quizId}`;

  if (loading) return <div className="min-h-screen bg-main flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-500 animate-spin" /></div>;
  if (!quiz) return <div className="min-h-screen bg-main flex items-center justify-center"><p className="text-muted">Quiz not found</p></div>;

  // Preview Mode
  if (previewMode) {
    if (showResults && results) {
      return (
        <div className="min-h-screen bg-main flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl p-8 max-w-lg w-full text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl font-bold text-emerald-500">{results.score}%</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground mb-2">{results.tier?.title}</h2>
            <p className="text-muted mb-6">{results.tier?.description}</p>
            <div className="bg-card-hover rounded-xl p-4 text-left mb-6">
              <h3 className="font-medium text-foreground mb-2">Your Next Step:</h3>
              <p className="text-sm text-muted">{results.tier?.nextStep}</p>
            </div>
            <button onClick={() => { setPreviewMode(false); setShowResults(false); setCurrentQuestion(0); setAnswers({}); }} className="btn-secondary">
              Exit Preview
            </button>
          </div>
        </div>
      );
    }

    const question = quiz.questions?.[currentQuestion];
    const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;

    return (
      <div className="min-h-screen bg-main flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-2xl p-8 max-w-lg w-full">
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm text-muted mb-2">
              <span>Question {currentQuestion + 1} of {quiz.questions.length}</span>
              <button onClick={() => { setPreviewMode(false); setCurrentQuestion(0); setAnswers({}); }} className="text-primary hover:underline">Exit Preview</button>
            </div>
            <div className="h-2 bg-card-hover rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <h3 className="font-heading text-xl font-semibold text-foreground mb-6">{question?.question}</h3>

          {question?.type === 'multiple_choice' && (
            <div className="space-y-3">
              {question.options?.map((option, i) => (
                <button
                  key={i}
                  onClick={() => setAnswers({ ...answers, [question.id]: option })}
                  className={cn(
                    "w-full p-4 rounded-xl border text-left transition-all",
                    answers[question.id] === option
                      ? "border-emerald-500 bg-emerald-500/10"
                      : "border-border bg-card-hover hover:border-emerald-500/50"
                  )}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {question?.type === 'yes_no' && (
            <div className="flex gap-4">
              {['Yes', 'No'].map((option) => (
                <button
                  key={option}
                  onClick={() => setAnswers({ ...answers, [question.id]: option })}
                  className={cn(
                    "flex-1 p-4 rounded-xl border font-medium transition-all",
                    answers[question.id] === option
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-500"
                      : "border-border bg-card-hover hover:border-emerald-500/50 text-foreground"
                  )}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {question?.type === 'slider' && (
            <div className="space-y-4">
              <input
                type="range"
                min="1"
                max="10"
                value={answers[question.id] || 5}
                onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-muted">
                <span>1</span>
                <span className="text-foreground font-semibold">{answers[question.id] || 5}</span>
                <span>10</span>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <button 
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))} 
              disabled={currentQuestion === 0}
              className="btn-secondary"
            >
              Previous
            </button>
            {currentQuestion < quiz.questions.length - 1 ? (
              <button 
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                disabled={!answers[question?.id]}
                className="btn-primary bg-emerald-500 hover:bg-emerald-600"
              >
                Next
              </button>
            ) : (
              <button 
                onClick={calculateResults}
                disabled={!answers[question?.id]}
                className="btn-primary bg-emerald-500 hover:bg-emerald-600"
              >
                See Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Admin View
  return (
    <div className="min-h-screen bg-main">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/dashboard')} className="p-2 text-muted hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-foreground">{quiz.name}</h1>
            <p className="text-muted">{quiz.niche}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreviewMode(true)} className="btn-secondary flex items-center gap-2">
              <Target className="w-4 h-4" /> Preview
            </button>
            <button onClick={regenerate} disabled={regenerating} className="btn-secondary flex items-center gap-2">
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Regenerate
            </button>
          </div>
        </div>

        {quiz.status === 'generating' && (
          <div className="card p-8 text-center mb-6">
            <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mx-auto mb-4" />
            <p className="text-foreground font-medium">Generating your quiz...</p>
            <p className="text-sm text-muted">This may take a minute</p>
          </div>
        )}

        {quiz.status === 'completed' && (
          <>
            {/* Share Link */}
            <div className="card p-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Shareable Link</p>
                  <p className="text-sm text-muted truncate">{shareLink}</p>
                </div>
                <button 
                  onClick={() => copyToClipboard(shareLink, 'share-link')}
                  className="btn-secondary flex items-center gap-2"
                >
                  {copied === 'share-link' ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  Copy
                </button>
              </div>
            </div>

            {/* Questions */}
            <div className="mb-6">
              <h2 className="font-heading text-lg font-semibold text-foreground mb-4">Questions ({quiz.questions?.length})</h2>
              <div className="space-y-4">
                {quiz.questions?.map((q, i) => (
                  <div key={q.id} className="card p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-emerald-500">{i + 1}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-foreground mb-2">{q.question}</p>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-0.5 text-xs bg-card-hover rounded text-muted">{q.type.replace('_', ' ')}</span>
                          {q.options?.map((opt, j) => (
                            <span key={j} className="px-2 py-0.5 text-xs bg-card-hover rounded text-muted">{opt}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Result Tiers */}
            <div>
              <h2 className="font-heading text-lg font-semibold text-foreground mb-4">Result Tiers</h2>
              <div className="space-y-4">
                {quiz.result_tiers?.map((tier, i) => (
                  <div key={i} className="card p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-500 rounded font-medium">{tier.range}%</span>
                      <h3 className="font-medium text-foreground">{tier.title}</h3>
                    </div>
                    <p className="text-sm text-muted mb-2">{tier.description}</p>
                    <p className="text-sm text-muted"><strong>Next Step:</strong> {tier.nextStep}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default QuizView;
